/**
 * Pure logic for the admin balance-entry screen (STARSEED-CAPMARKETS-194).
 *
 * Separated from the component for the same reason `heroBalance.js` is: this
 * decides what number reaches a member's dashboard, and that decision should be
 * testable without rendering anything.
 *
 * Two rules carry over from the read side and matter just as much on the write
 * side:
 *
 * 1. **Absent is not zero.** A blank input is omitted from the CSV entirely, so
 *    the server's validator records it as `None`. Coercing it to `0` here would
 *    re-introduce CAPMARKETS-094 on the write path, where it is worse — a
 *    fabricated zero becomes an immutable ledger row rather than a bad render.
 * 2. **`as_of` never defaults.** A statement dated by its entry time makes a
 *    three-week-old figure look current (CAPMARKETS-112).
 */
import { toFiniteNumber } from "./heroBalance";

/**
 * Equity, derived from what a partner card actually carries.
 *
 * The console card has `Balance` and `Open Trades Profit` and no equity column
 * at all — CAPMARKETS-179's root cause. The derivation was confirmed to the
 * cent on 2026-08-24 against two of Zach's four cards, where the partner's own
 * `My Banking` field equalled `balance + openTradesProfit` exactly.
 *
 * Returns `null` when either input is absent: an equity we cannot derive must
 * stay unknown rather than fall back to the balance, which would overstate a
 * member holding an open loss by exactly that loss (on 2026-08-21 that would
 * have shown Jamie $29,702.11 he did not have).
 */
export const deriveEquity = (balance, openTradesProfit) => {
  // `parseMoneyInput`, NOT `toFiniteNumber`. The operator copies figures off a
  // console card that prints them as "112,951.02", and `Number("112,951.02")`
  // is NaN. Using the stricter parser here while `buildCsv` used the
  // separator-stripping one meant a comma produced a written balance and an
  // ABSENT equity — with no validation error, because `validateRow` also used
  // the tolerant parser. That is the CAPMARKETS-179 blanking exactly, arriving
  // through the screen built to prevent it. One parser, one answer.
  const b = parseMoneyInput(balance);
  const p = parseMoneyInput(openTradesProfit);
  if (b === null || p === null || Number.isNaN(b) || Number.isNaN(p)) return null;
  return Math.round((b + p) * 100) / 100;
};

/** A money input the operator left blank is ABSENT — never zero. */
export const parseMoneyInput = (raw) => {
  if (raw === null || raw === undefined) return null;
  const text = String(raw).replace(/[,$\s]/g, "").trim();
  if (text === "") return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : NaN;
};

export const ROW_FIELDS = ["accountRef", "balance", "openTradesProfit", "asOf"];

/**
 * Validate one entry row. Returns a map of field -> message; empty means valid.
 *
 * Deliberately returns per-field messages rather than a single string, because
 * the operator has to be able to see WHICH field is wrong on a form with four
 * of them.
 */
export const validateRow = (row) => {
  const errors = {};
  if (!String(row.accountRef || "").trim()) {
    errors.accountRef = "Account is required";
  }
  if (!String(row.asOf || "").trim()) {
    // No default. See rule 2 in the module docblock.
    errors.asOf = "Statement date is required — it is never assumed to be today";
  } else if (Number.isNaN(new Date(row.asOf).getTime())) {
    errors.asOf = "Not a valid date";
  } else if (new Date(row.asOf).getTime() > Date.now()) {
    errors.asOf = "A future date would win every latest-wins comparison permanently";
  }
  for (const field of ["balance", "openTradesProfit"]) {
    const parsed = parseMoneyInput(row[field]);
    if (Number.isNaN(parsed)) errors[field] = "Not a number";
  }
  if (parseMoneyInput(row.balance) === null) {
    errors.balance = "Balance is required";
  }
  /*
   * Open trades profit is REQUIRED, and a typed `0` satisfies it.
   *
   * Without this the row is submittable with a blank field: equity comes back
   * null, `buildCsv` writes an empty equity cell, and the API accepts it —
   * which is the CAPMARKETS-179 blank-equity path reopened through the screen
   * built to close it. A card that genuinely carries no open-trades line
   * cannot produce a trustworthy equity, and this surface should refuse it
   * rather than store a half-figure (179 `AC2`, enforced at entry).
   *
   * Note this is deliberately STRICTER than the model, which still allows
   * `equity: None` for a statement whose column was blank. The model describes
   * what may exist; this describes what an operator may create here.
   * (Copilot, PR #2447.)
   */
  if (!errors.openTradesProfit && parseMoneyInput(row.openTradesProfit) === null) {
    errors.openTradesProfit =
      "Required — enter 0 if the card shows no open trades. Without it equity " +
      "cannot be derived and the member's dashboard goes blank.";
  }
  return errors;
};

/**
 * Two headers, because the route decides what a row is KEYED on from the
 * column names it finds (CAPMARKETS-113). Emitting `account_login`
 * unconditionally meant a console display name typed into the Account field was
 * recorded as `ref_source: 'login'` — a row asserting it is keyed on a stable
 * partner identifier when it is keyed on an editable name, with none of the
 * disclosure CAPMARKETS-133 requires. The operator states which they have and
 * the CSV says so.
 */
const CSV_HEADERS = {
  login: "account_login,account_name,as_of,currency,balance,equity",
  display_name: "account_name,as_of,currency,balance,equity",
};

/** The `ref_column` value the route needs to key on a display name. */
export const DISPLAY_NAME_REF_COLUMN = "account_name";

/**
 * Escape a CSV field. An account label can legitimately contain a comma, and an
 * unescaped one shifts every later column by a position — silently changing
 * which number is the balance.
 */
const cell = (value) => {
  if (value === null || value === undefined) return "";
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

/**
 * Build the CSV the import route consumes.
 *
 * The screen posts to `POST /partner-statements/import` rather than to a JSON
 * sibling, so validation, `(partner, ref, as_of)` idempotency and member
 * resolution stay in exactly one implementation (CAPMARKETS-194 AC7). Building
 * a CSV in a browser looks odd; two importers with two behaviours would be
 * worse.
 *
 * A field whose value is absent is written as an EMPTY cell, which the server's
 * validator maps to `None`. It is never written as `0`.
 */
export const buildCsv = (rows, keyedBy = "login") => {
  const lines = [CSV_HEADERS[keyedBy] || CSV_HEADERS.login];
  for (const row of rows) {
    const balance = parseMoneyInput(row.balance);
    const equity = deriveEquity(row.balance, row.openTradesProfit);
    const ref = cell(String(row.accountRef || "").trim());
    const tail = [
      cell(row.asOf),
      cell(row.currency || "USD"),
      cell(balance === null || Number.isNaN(balance) ? "" : balance),
      cell(equity === null ? "" : equity),
    ];
    // Keyed on the display name, the ref IS the name column — emitting a
    // separate label would give the route two candidate name columns.
    lines.push(
      keyedBy === "display_name"
        ? [ref, ...tail].join(",")
        : [ref, cell(row.accountLabel || ""), ...tail].join(","),
    );
  }
  return `${lines.join("\n")}\n`;
};

/** Human summary of what a dry run says will happen to one member. */
export const describeProjection = (entry) => {
  if (!entry || typeof entry !== "object") return null;
  const before = toFiniteNumber(entry.before?.equity);
  const after = toFiniteNumber(entry.after?.equity);
  return {
    memberId: entry.member_id,
    before,
    after,
    delta: before === null || after === null ? null : Math.round((after - before) * 100) / 100,
    unchanged: before !== null && after !== null && before === after,
  };
};

/** The fields a screenshot fills in — and so the fields an operator's edit must survive. */
const SCREENSHOT_FIELDS = ["accountRef", "balance", "openTradesProfit"];
/** The two figures a screenshot reads. Two readings of one account are compared on these. */
const FIGURE_FIELDS = ["balance", "openTradesProfit"];

const refOf = (r) => String(r?.accountRef ?? "").trim();
const isEmptyValue = (v) => String(v ?? "").trim() === "";
/** Every screenshot block that has been filled into a row, oldest first. */
const proposalsOf = (r) => r?.proposals || [];
/** The upload a block came from: its id without the block index (`<upload>:<block>`). */
const uploadOf = (id) => {
  const s = String(id ?? "");
  const at = s.lastIndexOf(":");
  return at < 0 ? s : s.slice(0, at);
};

/** One field's two readings are the same value: a ref by its trimmed text, money by its number. */
const sameValue = (field, a, b) => {
  if (field !== "accountRef") {
    const x = parseMoneyInput(a);
    const y = parseMoneyInput(b);
    if (!Number.isNaN(x) && !Number.isNaN(y)) return x === y;
  }
  return String(a ?? "").trim() === String(b ?? "").trim();
};

/**
 * Whether a block could be the same reading as a row.
 *
 * Where a screenshot has already read a figure into the row, the block must agree
 * with THAT reading, not with whatever the field holds now. Comparing against the
 * current value let two things through (review of 53119886): a balance the
 * operator had cleared agreed with any card for the account, and a card matching
 * the operator's own correction joined a row whose earlier reading it
 * contradicted — which then claimed its screenshots agreed. Only where no
 * screenshot has read the field does the row's own value count: empty agrees, a
 * typed figure must match. A figure the block lacks is no disagreement.
 *
 * This is the only test a block from ANOTHER upload has to pass to land on an
 * existing row, and it is deliberately about the figures rather than the
 * account. The account match alone is not evidence: a member with one priced
 * account gets `margin: None` from the ranker, so every card within 35% of that
 * account's last balance arrives as a confident, pre-selected match to it — the
 * member's other account's card included.
 */
const figuresAgree = (row, proposal) =>
  FIGURE_FIELDS.every((field) => {
    if (!(field in proposal) || isEmptyValue(proposal[field])) return true;
    const read = row.fromScreenshot;
    if (read && field in read && !isEmptyValue(read[field])) {
      return sameValue(field, read[field], proposal[field]);
    }
    return isEmptyValue(row[field]) || sameValue(field, row[field], proposal[field]);
  });

/**
 * Put a screenshot proposal into a row, keeping every field the operator has
 * changed since a screenshot last filled it in (CAPMARKETS-239, review of
 * PR #2550).
 *
 * A field is the operator's when it holds something and that something is not
 * what the screenshot last read for it — a figure corrected by hand, an account
 * re-pointed by hand, or a value typed before any screenshot arrived. Re-applying
 * a proposal used to overwrite those silently; the importer keeps its proposals
 * and a live "Fill in" button, so that happened on every click. An empty field is
 * filled, and a reading with no figure never blanks one the row holds — nor
 * replaces any figure a screenshot read, so an agreement check always has the
 * earlier reading to compare against.
 *
 * A block's figures belong to the account it names. Once the operator has typed
 * a different account into the row, an empty figure field is left for them to
 * fill: a cleared card reused for the next account otherwise took the old card's
 * figures on the next click, under the new account, with no note on the figures
 * (review of a8dbda49).
 *
 * `fromScreenshot` records what the screenshot read, whether or not it was used,
 * so the page can show the difference (`screenshotDisagreements`). `proposals`
 * collects every block filled into the row, so a re-sent block finds it and the
 * page can say when more than one screenshot read it.
 */
const fillFromScreenshot = (row, proposal, memberId) => {
  const { accountRef, balance, openTradesProfit, proposal: from, ...other } = proposal;
  const incoming = { accountRef, balance, openTradesProfit };
  const read = { ...(row.fromScreenshot || {}) };
  const next = { ...row, ...other };
  for (const field of SCREENSHOT_FIELDS) {
    if (!(field in proposal)) continue;
    const current = row[field];
    const untouched = isEmptyValue(current)
      || (field in read && sameValue(field, current, read[field]));
    const blanking = isEmptyValue(incoming[field]) && !isEmptyValue(current);
    // `accountRef` comes first in SCREENSHOT_FIELDS, so `next` already names the
    // row's account when a figure is decided.
    const elsewhere = field !== "accountRef" && isEmptyValue(current)
      && Boolean(refOf(incoming)) && Boolean(refOf(next)) && refOf(next) !== refOf(incoming);
    next[field] = untouched && !blanking && !elsewhere ? incoming[field] : current;
    // A reading with no figure says nothing about the figure, so it is never
    // recorded over one: not over the figure the row holds (that made the page
    // say "your entry is kept" about a figure no one typed), and not over an
    // earlier reading when the field was cleared (that left `figuresAgree` nothing
    // to compare, so a card reading a different figure joined the row).
    const erasing = isEmptyValue(incoming[field]) && field in read && !isEmptyValue(read[field]);
    if (!blanking && !erasing) read[field] = incoming[field];
  }
  next.fromScreenshot = read;
  if (from && !proposalsOf(row).includes(from)) next.proposals = [...proposalsOf(row), from];
  if (memberId) next.memberId = memberId;
  return next;
};

/**
 * Merge screenshot proposals into the rows already on the page.
 *
 * STARSEED-CAPMARKETS-239. This used to be `setRows(incoming)` — a REPLACE.
 * The partner sends one image per account (Milen did exactly that on
 * 2026-09-05), so the natural operator move is upload, fill in, upload the
 * second, fill in. Under replace, the second fill silently discarded the first
 * account's row: the operator writes one account believing they wrote two, and
 * nothing on screen says otherwise.
 *
 * The rule under every clause here: where the merge cannot tell which of two
 * figures is right, it keeps both where the operator can see them. It never
 * picks. That is the import route's rule too — a file carrying two figures for
 * one account on one date is refused whole, because "picking one would be a
 * guess" (`routes/partner_statements.py`).
 *
 * A proposal looks for its row only among the rows that existed BEFORE this
 * apply — never among rows this same apply has just added — in two passes:
 *
 *  1. **The same block, filled in again, finds the row it made.** Every row
 *     collects the `proposals` filled into it, and the importer re-sends every
 *     ready block on each click. This pass runs for EVERY block before the
 *     next one starts: interleaved in block order, an earlier block with no row
 *     could claim by account the row a later block owns, and carry the
 *     operator's correction of that later block onto the wrong account.
 *  2. **A block with no row joins one for the same account only when nothing it
 *     read disagrees with that row** (`figuresAgree`): the same figures read
 *     again, or figures for fields the row lacks. The account must also be
 *     unambiguous — once in this apply, and on exactly one unclaimed existing
 *     row of the same member. Matching is on the trimmed account ref, never the
 *     label, which is operator-editable (CAPMARKETS-133). A row that already
 *     holds another block of the same upload is never that row: two blocks of one
 *     screenshot are two cards, not one card read twice.
 *  3. **Everything else is appended**: two blocks for one account, a DIFFERENT
 *     figure for an account already here, and a proposal with no account ref.
 *
 * So a second figure for an account is a second row, whichever upload it came
 * from. The first version of this function let a block write over the row the
 * block before it had just appended; the second let a later upload write over
 * an earlier screenshot's row whenever the account was unique — which is exactly
 * how a card for the member's other account, pre-matched to the only priced
 * one, lands. Both times a figure disappeared and nothing on screen changed. Two
 * rows reach the import and are refused, and `duplicateAccountRefs` lets the
 * page name the account before that. A corrected screenshot is therefore a
 * second row too, until the operator removes the one that is wrong: the merge
 * cannot tell a correction from a mismatch, and guessing is the defect.
 *
 * A matched row keeps what the operator changed (`fillFromScreenshot`). Rows
 * with nothing in them are dropped — the page seeds one, and keeping it would
 * fail validation and block the write for a row nobody filled in. A row with
 * anything in it is kept.
 */
export const mergeProposals = (existing, incoming, makeBlank, { memberId = null } = {}) => {
  const isBlank = (r) =>
    !refOf(r) && isEmptyValue(r?.balance) && isEmptyValue(r?.openTradesProfit)
    && isEmptyValue(r?.accountLabel);

  const before = (existing || []).filter((r) => !isBlank(r)).map((r) => ({ ...r }));
  const batch = incoming || [];
  const inBatch = new Map();
  for (const p of batch) {
    const k = refOf(p);
    if (k) inBatch.set(k, (inBatch.get(k) || 0) + 1);
  }

  const target = batch.map(() => -1);
  const claimed = new Set();

  // Pass 1: every block that has a row finds it, before any account match.
  batch.forEach((p, j) => {
    if (!p.proposal) return;
    const at = before.findIndex((r, i) => !claimed.has(i) && proposalsOf(r).includes(p.proposal));
    if (at >= 0) {
      target[j] = at;
      claimed.add(at);
    }
  });

  // Pass 2: a block with no row may join one row for its account that it agrees with.
  batch.forEach((p, j) => {
    const k = refOf(p);
    if (target[j] >= 0 || !k || inBatch.get(k) !== 1) return;
    const upload = p.proposal ? uploadOf(p.proposal) : null;
    const sameAccount = before
      .map((r, i) => i)
      .filter((i) => !claimed.has(i) && refOf(before[i]) === k
        && (!before[i].memberId || !memberId || before[i].memberId === memberId)
        // A sibling's row (review of a8dbda49): joining it put one block id on two
        // rows once the sibling was re-pointed, and a later skip named one account twice.
        && !(upload !== null && proposalsOf(before[i]).some((id) => uploadOf(id) === upload)));
    if (sameAccount.length === 1 && figuresAgree(before[sameAccount[0]], p)) {
      target[j] = sameAccount[0];
      claimed.add(sameAccount[0]);
    }
  });

  const appended = [];
  batch.forEach((p, j) => {
    if (target[j] >= 0) before[target[j]] = fillFromScreenshot(before[target[j]], p, memberId);
    else appended.push(fillFromScreenshot(makeBlank(), p, memberId));
  });
  const out = [...before, ...appended];
  return out.length ? out : [makeBlank()];
};

/**
 * Account refs that appear on more than one row, in first-seen order.
 *
 * The import refuses the whole file when one account appears twice for a date,
 * and its refusal names line numbers the operator never saw. This names the
 * account while the rows are still on screen.
 */
export const duplicateAccountRefs = (rows) => {
  const counts = new Map();
  for (const r of rows || []) {
    const k = refOf(r);
    if (k) counts.set(k, (counts.get(k) || 0) + 1);
  }
  return [...counts].filter(([, n]) => n > 1).map(([k]) => k);
};

/**
 * `{ field: whatTheScreenshotRead }` for each field where the row now holds
 * something else. Empty when the row was never filled from a screenshot, or
 * still says what it read.
 */
export const screenshotDisagreements = (row) => {
  const read = row?.fromScreenshot;
  const out = {};
  if (!read) return out;
  for (const field of SCREENSHOT_FIELDS) {
    if (field in read && !isEmptyValue(row[field]) && !sameValue(field, row[field], read[field])) {
      out[field] = read[field];
    }
  }
  return out;
};

/** How many screenshot uploads have been read into a row. More than one means they agreed. */
export const screenshotReadings = (row) => proposalsOf(row).length;

/**
 * Remove a row, and remember which blocks it came from and which account they
 * named. The importer keeps those blocks ready and re-sends them on every
 * "Fill in", so without this a row the operator deliberately removed came back
 * on the next click, unannounced, and would be previewed and written.
 */
export const removeRow = (editor, key) => {
  const row = editor.rows.find((r) => r.key === key);
  const removed = { ...(editor.removed || {}) };
  if (row) {
    const named = refOf(row.fromScreenshot || row);
    for (const id of proposalsOf(row)) removed[id] = named;
  }
  return { ...editor, rows: editor.rows.filter((r) => r.key !== key), removed };
};

/** A row with nothing in it: no account, no label, no figures. */
const isBlankRow = (r) =>
  !refOf(r) && isEmptyValue(r?.balance) && isEmptyValue(r?.openTradesProfit)
  && isEmptyValue(r?.accountLabel);

/**
 * Merge a fill-in, skipping any block whose row the operator removed while the
 * block still names the account it named then. A block since re-pointed to
 * another account in the importer is a new decision, and is filled in.
 * `skipped` lists the accounts not filled in again, for the page to say so.
 *
 * A screenshot row the operator cleared to blank counts as removed. On the last
 * row the page offers no Remove, so clearing it is how that row is removed, and
 * without this its block came back on the next click (review of 53119886).
 */
const mergeFillIn = (editor, incoming, makeBlank, memberId) => {
  const removed = { ...(editor.removed || {}) };
  for (const r of editor.rows) {
    if (!isBlankRow(r)) continue;
    const named = refOf(r.fromScreenshot || r);
    for (const id of proposalsOf(r)) removed[id] = named;
  }
  const accepted = [];
  const skipped = [];
  for (const p of incoming || []) {
    if (p.proposal && p.proposal in removed) {
      if (removed[p.proposal] === refOf(p)) {
        skipped.push(refOf(p));
        continue;
      }
      delete removed[p.proposal];
    }
    accepted.push(p);
  }
  return { rows: mergeProposals(editor.rows, accepted, makeBlank, { memberId }), removed, skipped };
};

/** The key for two members the operator chose to keep side by side, in either order. */
const pairOf = (a, b) => [a, b].sort().join("|");

/**
 * The rows a fill-in for `memberId` must ask about: rows with something in them
 * that a screenshot filled in for ANOTHER member, unless the operator has chosen
 * to keep that pair of members side by side. Grouped by member, in page order.
 */
const rowsToAskAbout = (editor, memberId) => {
  const others = [];
  if (!memberId) return others;
  const pairs = editor.mixed || [];
  for (const r of editor.rows) {
    if (!r.memberId || r.memberId === memberId || isBlankRow(r)) continue;
    if (pairs.includes(pairOf(r.memberId, memberId))) continue;
    let entry = others.find((o) => o.memberId === r.memberId);
    if (!entry) {
      entry = { memberId: r.memberId, rows: [] };
      others.push(entry);
    }
    entry.rows.push(r);
  }
  return others;
};

/**
 * Keep-both pairs are forgotten once neither member has a row with anything in
 * it: an approval given for rows that are all gone does not carry over to a new
 * statement for the same two members.
 */
const pruneMixed = (editor) => {
  const present = new Set(
    editor.rows.filter((r) => r.memberId && !isBlankRow(r)).map((r) => r.memberId),
  );
  return {
    ...editor,
    mixed: (editor.mixed || []).filter((pair) => pair.split("|").some((m) => present.has(m))),
  };
};

/**
 * The member question as it stands NOW — `{ memberId, incoming, others }` — or
 * null when none is waiting.
 *
 * Only the waiting fill-in is stored. The rows the question names are worked out
 * from the current rows each time it is shown, and again when it is answered, so
 * what it lists and what Replace removes are the same rows by construction.
 * Storing the list when the question was asked let Replace remove a row filled in
 * for that member while the question waited, which the question never named
 * (review of 53119886).
 */
export const questionFor = (editor) => {
  if (!editor.pending) return null;
  const current = pruneMixed(editor);
  return {
    memberId: editor.pending.memberId,
    incoming: editor.pending.incoming,
    others: rowsToAskAbout(current, editor.pending.memberId),
  };
};

/**
 * Apply a screenshot fill-in to the page's editor state:
 * `{ rows, pending, held, removed, skipped, mixed }`.
 *
 * Rows remember the member their screenshot was ranked for, and a fill-in for a
 * DIFFERENT member does not merge. It waits in `pending` until the operator
 * chooses (`resolvePendingApply`). Under the merge alone, a fill-in for the
 * wrong member survived the redo: the operator picked the wrong member, filled
 * in, switched to the right one and filled in again — and the wrong member's
 * account kept a row carrying the right member's figure, beside the correct one.
 * The replace this merge superseded had cleaned that up by accident. That is the
 * cross-member write CAPMARKETS-230 exists to prevent, and whether the earlier
 * rows are a mistake or a second member's statement is not something to guess.
 *
 * Keep both is remembered per pair of members (`mixed`), never per member, so
 * approving one pair does not silence another. One question at a time: while
 * one waits, a fill-in for another member that would need its own question is
 * held — nothing is filled in, and `held` lists its member for the page to say
 * so — and a fill-in that needs no question merges past it. Only a newer fill-in
 * for the waiting member replaces the waiting one.
 *
 * A member stays in `held` until a fill-in for that member merges or raises its
 * own question, or a write starts the page over. Answering the question, or
 * merging another member's rows, does not fill the held member in, and changing
 * the importer's member discards the held upload — so clearing the note then
 * left nothing on the page saying that statement was never filled in (review of
 * a8dbda49).
 *
 * A row no screenshot has filled in carries no member and never raises this.
 */
export const applyScreenshot = (editor, incoming, source, makeBlank) => {
  const memberId = source?.memberId || null;
  const current = pruneMixed(editor);
  // This member's fill-in is dealt with below — held again, asked, or merged.
  const held = (current.held || []).filter((m) => m !== memberId);
  if (rowsToAskAbout(current, memberId).length) {
    if (current.pending && current.pending.memberId !== memberId) {
      return { ...current, held: [...held, memberId] };
    }
    return { ...current, pending: { incoming, memberId }, held };
  }
  const merged = mergeFillIn(current, incoming, makeBlank, memberId);
  const pending = current.pending && current.pending.memberId !== memberId ? current.pending : null;
  return { ...current, ...merged, pending, held };
};

/**
 * The operator's answer to the waiting member question.
 *
 * - `"replace"` removes exactly the rows the question names as it stands (see
 *   `questionFor`), then merges. That includes a typed row a screenshot for
 *   another member later filled, because that row now carries that member's
 *   screenshot figures; a row no screenshot has touched is never named and stays.
 * - `"keep"` merges beside them, and remembers each named member paired with the
 *   waiting one.
 * - `"cancel"` drops the fill-in and changes no row.
 *
 * No answer clears another member's held fill-in (see `applyScreenshot`).
 */
export const resolvePendingApply = (editor, choice, makeBlank) => {
  const current = pruneMixed(editor);
  const question = questionFor(current);
  if (!question) return editor;
  if (choice === "cancel") return { ...current, pending: null };
  const held = (current.held || []).filter((m) => m !== question.memberId);
  if (choice === "replace") {
    const named = new Set(question.others.flatMap((o) => o.rows.map((r) => r.key)));
    const base = { ...current, rows: current.rows.filter((r) => !named.has(r.key)) };
    return {
      ...base,
      ...mergeFillIn(base, question.incoming, makeBlank, question.memberId),
      pending: null,
      held,
    };
  }
  return {
    ...current,
    ...mergeFillIn(current, question.incoming, makeBlank, question.memberId),
    mixed: [...new Set([
      ...(current.mixed || []),
      ...question.others.map((o) => pairOf(question.memberId, o.memberId)),
    ])],
    pending: null,
    held,
  };
};
